import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'react-phone-input-2/lib/style.css'
import axios from 'axios';
import TailwindLayout from 'TailwindcssComponents/TailwindLayout';
import TopBar from 'TailwindcssComponents/TopBar';
import FooterTailwind from 'TailwindcssComponents/Footer';
import { Icon } from "@iconify/react";

export default function EditEventOrganization() {
    const { id } = useParams();
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const navigate = useNavigate();
    const [coverImagePreview, setCoverImagePreview] = useState(null);
    const [organizationImagePreview, setOrganizationImagePreview] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [socialLinks, setSocialLinks] = useState([]);
    const [initialSlug, setInitialSlug] = useState('');
    const [initialName, setInitialName] = useState('');
    const [loading, setLoading] = useState(true);
    const prevSlugRef = useRef('');
    const debounceTimer = useRef(null);
    const socialPlatforms = [
        { name: 'Instagram', prefix: 'https://instagram.com/' },
        { name: 'Facebook', prefix: 'https://facebook.com/' },
        { name: 'YouTube', prefix: 'https://youtube.com/' },
        { name: 'TikTok', prefix: 'https://tiktok.com/@' },
        { name: 'LinkedIn', prefix: 'https://linkedin.com/in/' },
        { name: 'Twitter', prefix: 'https://twitter.com/' }
    ];

    const handleAddSocialLink = () => {
        setSocialLinks([...socialLinks, { platform: '', url: '' }]);
    };

    const handleSocialLinkChange = (index, field, value) => {
        const updatedLinks = socialLinks.map((link, i) =>
            i === index ? { ...link, [field]: value } : link
        );
        setSocialLinks(updatedLinks);
        editOrganization.setFieldValue("socialLinks", updatedLinks); // Updates Formik state
    };

    const handleRemoveSocialLink = (index) => {
        setSocialLinks(socialLinks.filter((_, i) => i !== index));
    };

    const handleOrganizationImageChange = (event) => {
        const file = event.currentTarget.files[0];
        editOrganization.setFieldValue('orgImage', file);
        editOrganization.setFieldTouched('orgImage', true);
        if (file) {
            setOrganizationImagePreview(URL.createObjectURL(file));
        }
    };

    const handleCoverImageChange = (event) => {
        const file = event.currentTarget.files[0];
        editOrganization.setFieldValue('coverImage', file);
        editOrganization.setFieldTouched('coverImage', true);
        if (file) {
            setCoverImagePreview(URL.createObjectURL(file));
        }
    };

    const fetchOrganizationData = async () => {
        if (!bearerToken) return;
        setLoading(true);
        try {
            const response = await axios.get(`${apiUrl}auth-service/org/${id}`, {
                headers: { Authorization: `Bearer ${bearerToken}` }
            });
            if (response.data?.success && response.data?.data) {
                const organization = response.data?.data;
                const socialMedia = JSON.parse(organization.socialMedia) || {};
                const socialLinksArray = Object.entries(socialMedia).map(([platform, url]) => ({
                    platform,
                    url,
                }));
                setSocialLinks(socialLinksArray);
                editOrganization.setValues({
                    name: organization.name,
                    slug: organization.slug,
                    email: organization.email,
                    phone: organization.phone,
                    address: organization.address,
                    website: organization.website,
                    orgImage: organization.orgImage?.url,
                    coverImage: organization.coverImage?.url,
                });
                setOrganizationImagePreview(organization.orgImage?.url);
                setCoverImagePreview(organization.coverImage?.url);
                setInitialName(organization.name);
                setInitialSlug(organization.slug);
            } else {
                throw new Error("Failed to fetch organization data");
            }
        } catch (error) {
            console.log(error.response?.data?.message || error.message);
        }
        finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrganizationData();
    }, [id]);

    const editOrganization = useFormik({
        initialValues: {
            name: '',
            slug: '',
            email: '',
            phone: '',
            address: '',
            website: '',
            coverImage: null,
            orgImage: null,
        },
        validationSchema: Yup.object({
            name: Yup.string()
                .required('Required'),
            slug: Yup.string().required("Required"),
            phone: Yup.string()
                .required("Required")
                .matches(/^\+94\d{9}$/, "Phone number must start with +94 and contain 9 digits after it")
                .length(12, "Phone number must be exactly 12 characters"),
            email: Yup.string()
                .required('Required')
                .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Enter a valid email')
                .email('Enter a valid email'),
            website: Yup.string()
                .nullable()
                .matches(/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/, {
                    message: 'Enter a valid URL',
                }),
            socialLinks: Yup.array().of(
                Yup.object().shape({
                    platform: Yup.string().required('Please select a platform'),
                    url: Yup.string()
                        .required('Social media link is required')
                        .matches(
                            /^(https?:\/\/)?([\w.-]+)\.([a-z.]{2,6})(:[0-9]{1,5})?(\/[\w@\/.-]*)*\/?$/,
                            'Enter a valid social media URL'
                        ),
                })
            ),
            coverImage: Yup.mixed()
                .nullable()
                .notRequired()
                .typeError("Please upload a valid image")
                .test("validCoverImage", "Invalid cover image", (value) => {
                    if (!value) return true; // Not required
                    if (typeof value === "object") return true; // File object (new upload)
                    if (typeof value === "string" && value.startsWith("http")) return true; // Existing image URL
                    return false;
                })
                .test("fileType", "Only image files are allowed", (value) => {
                    if (!value || typeof value === "string") return true; // Allow existing image URLs or empty
                    return ["image/jpeg", "image/png", "image/gif"].includes(value.type);
                })
                .test("fileSize", "File size must be less than 1MB", (value) => {
                    if (!value || typeof value === "string") return true; // Allow existing URLs or no file
                    return value.size <= 1 * 1024 * 1024;
                }),
            orgImage: Yup.mixed()
                .nullable()
                .notRequired()
                .typeError("Please upload a valid image")
                .test("validOrgImage", "Invalid org image", (value) => {
                    if (!value) return true; // Not required
                    if (typeof value === "object") return true; // File object (new upload)
                    if (typeof value === "string" && value.startsWith("http")) return true; // Existing image URL
                    return false;
                })
                .test("fileType", "Only image files are allowed", (value) => {
                    if (!value || typeof value === "string") return true; // Allow existing image URLs or empty
                    return ["image/jpeg", "image/png", "image/gif"].includes(value.type);
                })
                .test("fileSize", "File size must be less than 1MB", (value) => {
                    if (!value || typeof value === "string") return true; // Allow existing URLs or no file
                    return value.size <= 1 * 1024 * 1024;
                }),
        }),
        onSubmit: async (values, { resetForm }) => {
            setIsLoading(true);
            try {
                const formData = new FormData();
                formData.append('name', values.name);
                formData.append('slug', values.slug);
                formData.append('email', values.email);
                formData.append('phone', values.phone);
                formData.append('address', values.address || "");
                formData.append('website', values.website || "");
                const socialMediaJSON = socialLinks.reduce((acc, link) => {
                    if (link.platform && link.url) {
                        acc[link.platform] = link.url;
                    }
                    return acc;
                }, {});
                formData.append('socialMedia', JSON.stringify(socialMediaJSON));

                if (values.orgImage) {
                    formData.append('orgImage', values.orgImage);
                }
                if (values.coverImage) {
                    formData.append('coverImage', values.coverImage);
                }

                const response = await axios.put(`${apiUrl}auth-service/org/${id}`, formData,
                    {
                        headers: { 'Authorization': `Bearer ${bearerToken}` }
                    });
                if (response.data?.success) {
                    toast.success(response.data?.message || 'Organization has been successfully updated!');
                    resetForm();
                    setTimeout(() => {
                        navigate(-1);
                    }, 1500);
                } else {
                    throw new Error('Failed to update organization');
                }
            } catch (error) {
                console.error('Error submitting form:', error.message);
                toast.error(error.response.data?.message || error.message);
                setIsLoading(false);
            } finally {
                setIsLoading(false);
            }
        },
    });

    const validateSlug = async (slug, setFieldError, setFieldTouched) => {
        if (!slug || (slug === initialSlug)) return;
        setFieldTouched("slug", true);
        try {
            const response = await axios.get(`${apiUrl}auth-service/org/check-slug/${slug}`);
            if (!response.data.success) {
                setFieldError("slug", response.data.message || "Slug is not available");
            }
        } catch (error) {
            if (error.response?.status === 400) {
                setFieldError("slug", error.response.data.message || "Slug already taken");
            } else {
                setFieldError("slug", "Error validating slug. Please try again.");
            }
        }
    };

    useEffect(() => {
        const name = editOrganization?.values?.name?.trim?.() || '';
        const currentSlug = editOrganization?.values?.slug?.trim?.() || '';

        // Generate slug from name only if slug hasn't been manually edited
        if (name && name !== initialName && currentSlug === initialSlug) {
            const generatedSlug = name
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^a-z0-9-]/g, '');

            if (generatedSlug !== currentSlug) {
                editOrganization.setFieldValue('slug', generatedSlug);
                return;
            }
        }

        // Debounced validation when slug changes
        if (currentSlug && currentSlug !== prevSlugRef.current && currentSlug !== initialSlug) {
            prevSlugRef.current = currentSlug;

            if (debounceTimer.current) clearTimeout(debounceTimer.current);

            debounceTimer.current = setTimeout(() => {
                validateSlug(currentSlug, editOrganization.setFieldError, editOrganization.setFieldTouched);
            }, 300);
        }
    }, [editOrganization.values.name, editOrganization.values.slug]);

    return (
        <TailwindLayout>
            <TopBar />
            <div className="max-w-7xl mx-auto p-12 sm:p-12 lg:p-12 mb-8">
                {loading ? (<div className="bg-white rounded-lg shadow-lg overflow-hidden p-6 space-y-6">

                    {/* Header */}
                    <div className="h-6 w-1/3 bg-gray-300 rounded animate-pulse"></div>

                    {/* Two-column layout */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t pt-6">

                        {/* Left Column - Form Fields */}
                        <div className="space-y-4">
                            <div className="h-10 bg-gray-300 rounded animate-pulse"></div>
                            <div className="h-10 bg-gray-300 rounded animate-pulse"></div>
                            <div className="h-10 bg-gray-300 rounded animate-pulse"></div>
                            <div className="h-10 bg-gray-300 rounded animate-pulse"></div>
                            <div className="h-10 bg-gray-300 rounded animate-pulse"></div>
                        </div>

                        {/* Right Column - Image Uploads */}
                        <div className="space-y-6">
                            <div className="w-32 h-32 bg-gray-300 rounded-md animate-pulse"></div>
                            <div className="w-32 h-32 bg-gray-300 rounded-md animate-pulse"></div>
                        </div>
                    </div>

                    {/* Social Media Section */}
                    <div className="space-y-4">
                        {[...Array(2)].map((_, i) => (
                            <div key={i} className="flex gap-4">
                                <div className="flex-1 h-10 bg-gray-300 rounded animate-pulse"></div>
                                <div className="flex-1 h-10 bg-gray-300 rounded animate-pulse"></div>
                                <div className="w-10 h-10 bg-gray-300 rounded animate-pulse"></div>
                            </div>
                        ))}
                        <div className="h-10 w-48 bg-gray-300 rounded animate-pulse"></div>
                    </div>

                    {/* Submit Button */}
                    <div className="w-32 h-10 bg-gray-300 rounded animate-pulse"></div>
                </div>) : (

                    <form onSubmit={editOrganization.handleSubmit}>
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden">

                            <div className="p-6">
                                <div className="space-y-6">
                                    {/* Organization Info Section */}
                                    <div>
                                        <h2 className="text-xl font-semibold mb-4">Edit Organization</h2>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t pt-6">
                                            {/* Left Column - Form Fields */}
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-sm font-medium mb-2">
                                                        Name <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        value={editOrganization.values.name}
                                                        onChange={editOrganization.handleChange}
                                                        onBlur={editOrganization.handleBlur}
                                                        placeholder="Organization Name"
                                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                                    />
                                                    {editOrganization.touched.name && editOrganization.errors.name && <div className="text-red-500">{editOrganization.errors.name}</div>}
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium mb-2">
                                                        Unique Name <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="slug"
                                                        value={editOrganization.values.slug}
                                                        onChange={editOrganization.handleChange}
                                                        onBlur={() => {
                                                            const currentSlug = editOrganization.values.slug.trim();
                                                            if (currentSlug && currentSlug !== initialSlug) {
                                                                validateSlug(currentSlug, editOrganization.setFieldError, editOrganization.setFieldTouched);
                                                            }
                                                        }}
                                                        placeholder="eg. the-grand-organization"
                                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                                    />
                                                    {editOrganization.touched.slug && editOrganization.errors.slug && <div className="text-red-500">{editOrganization.errors.slug}</div>}
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium mb-2">
                                                        Contact Email <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        placeholder="e.g. abdc@zmail.com"
                                                        value={editOrganization.values.email}
                                                        onChange={editOrganization.handleChange}
                                                        onBlur={editOrganization.handleBlur}
                                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                                    />
                                                    {editOrganization.touched.email && editOrganization.errors.email && <div className="text-red-500">{editOrganization.errors.email}</div>}
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium mb-2">
                                                        Contact Phone <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="tel"
                                                        name="phone"
                                                        placeholder="e.g. +1234567890"
                                                        value={editOrganization.values.phone}
                                                        onChange={editOrganization.handleChange}
                                                        onBlur={editOrganization.handleBlur}
                                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                                    />
                                                    {editOrganization.touched.phone && editOrganization.errors.phone && <div className="text-red-500">{editOrganization.errors.phone}</div>}
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium mb-2">
                                                        Address
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="address"
                                                        placeholder="Address"
                                                        value={editOrganization.values.address}
                                                        onChange={editOrganization.handleChange}
                                                        onBlur={editOrganization.handleBlur}
                                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                                    />
                                                    {editOrganization.touched.address && editOrganization.errors.address && <div className="text-red-500">{editOrganization.errors.address}</div>}
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium mb-2">
                                                        Website Link
                                                    </label>
                                                    <input
                                                        type="url"
                                                        name="website"
                                                        placeholder="e.g. https://example.com"
                                                        value={editOrganization.values.website}
                                                        onChange={editOrganization.handleChange}
                                                        onBlur={editOrganization.handleBlur}
                                                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                                    />
                                                    {editOrganization.touched.website && editOrganization.errors.website && <div className="text-red-500">{editOrganization.errors.website}</div>}
                                                </div>
                                            </div>

                                            {/* Right Column - Image Uploads */}
                                            <div className="space-y-6">
                                                <div>
                                                    <h4 className="text-sm font-medium mb-2">Logo Image <span className="text-gray-500">(Recommended size: below 1mb, 1:1, JPG/JPEG/PNG format)</span></h4>
                                                    <div className="flex items-center space-x-4">
                                                        <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center relative">
                                                            {organizationImagePreview ? (
                                                                <>
                                                                    <img src={organizationImagePreview} alt="Organization Logo" className="w-full h-full object-cover rounded-md" />
                                                                    {/* <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setOrganizationImagePreview(null);
                                                                        editOrganization.setFieldValue('orgImage', null);
                                                                        editOrganization.setFieldTouched('orgImage', true);
                                                                    }}
                                                                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg transition-colors"
                                                                    aria-label="Remove image"
                                                                >
                                                                    <Icon icon="material-symbols-light:close-rounded" className="text-lg" />
                                                                </button> */}
                                                                </>
                                                            ) : (
                                                                <Icon icon="iconoir:add-media-image" className="text-xl" />
                                                            )}
                                                        </div>
                                                        <label className="inline-flex items-center px-4 py-2 border border-blue-500 text-blue-500 rounded-md hover:bg-blue-50 cursor-pointer">
                                                            <span>{organizationImagePreview ? 'Change' : 'Upload'}</span>
                                                            <input type="file" className="hidden" onChange={handleOrganizationImageChange} />
                                                        </label>
                                                    </div>
                                                    {editOrganization.touched.orgImage && editOrganization.errors.orgImage && (
                                                        <p className="text-red-500 text-sm mt-1">{editOrganization.errors.orgImage}</p>
                                                    )}
                                                </div>

                                                <div>
                                                    <h4 className="text-sm font-medium mb-2">Cover Image <span className="text-gray-500">(Recommended size: below 1mb, 4:1, JPG/JPEG/PNG format)</span></h4>
                                                    <div className="flex items-center space-x-4">
                                                        <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center relative">
                                                            {coverImagePreview ? (
                                                                <>
                                                                    <img src={coverImagePreview} alt="Cover" className="w-full h-full object-cover rounded-md" />
                                                                    {/* <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setCoverImagePreview(null);
                                                                        editOrganization.setFieldValue('coverImage', '');
                                                                        editOrganization.setFieldTouched('coverImage', true);
                                                                    }}
                                                                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg transition-colors"
                                                                    aria-label="Remove image"
                                                                >
                                                                    <Icon icon="material-symbols-light:close-rounded" className="text-lg" />
                                                                </button> */}
                                                                </>
                                                            ) : (
                                                                <Icon icon="iconoir:add-media-image" className="text-xl" />
                                                            )}
                                                        </div>
                                                        <label className="inline-flex items-center px-4 py-2 border border-blue-500 text-blue-500 rounded-md hover:bg-blue-50 cursor-pointer">
                                                            <span>{coverImagePreview ? 'Change' : 'Upload'}</span>
                                                            <input type="file" className="hidden" onChange={handleCoverImageChange} />
                                                        </label>
                                                    </div>
                                                    {editOrganization.touched.coverImage && editOrganization.errors.coverImage && (
                                                        <p className="text-red-500 text-sm mt-1">{editOrganization.errors.coverImage}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Social Media Links Section */}
                                    <div>
                                        <h3 className="font-medium text-sm">Social Media Links</h3>
                                        <div className="space-y-4">
                                            {socialLinks.map((link, index) => (
                                                <div key={index} className="group relative flex items-baseline gap-4 p-2 rounded-lg">
                                                    {/* Platform Dropdown */}
                                                    <div className="flex-1">
                                                        <label className="block text-sm font-medium mb-1">
                                                            Platform
                                                        </label>
                                                        <div className='relative'>
                                                            <select
                                                                value={link.platform}
                                                                onChange={(e) => handleSocialLinkChange(index, 'platform', e.target.value)}
                                                                className="w-full h-10 px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                                            >
                                                                <option value="">Select Platform</option>
                                                                {socialPlatforms.map((platform) => (
                                                                    <option key={platform.name} value={platform.name}>
                                                                        {platform.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {editOrganization.errors.socialLinks &&
                                                                editOrganization.errors.socialLinks[index] &&
                                                                editOrganization.errors.socialLinks[index].platform && (
                                                                    <div className="text-red-500 text-sm">
                                                                        {editOrganization.errors.socialLinks[index].platform}
                                                                    </div>
                                                                )}
                                                        </div>
                                                    </div>

                                                    {/* URL Input */}
                                                    <div className="flex-1">
                                                        <label className="block text-sm font-medium mb-1">
                                                            Link
                                                        </label>
                                                        <div className="relative">
                                                            <input
                                                                type="text"
                                                                placeholder="e.g. https://instagram.com/username"
                                                                value={link.url}
                                                                onChange={(e) => handleSocialLinkChange(index, 'url', e.target.value)}
                                                                className="w-full h-10 px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300"
                                                            />
                                                            {editOrganization.errors.socialLinks &&
                                                                editOrganization.errors.socialLinks[index] &&
                                                                editOrganization.errors.socialLinks[index].url && (
                                                                    <div className="text-red-500 text-sm">
                                                                        {editOrganization.errors.socialLinks[index].url}
                                                                    </div>
                                                                )}
                                                        </div>
                                                    </div>

                                                    {/* Remove Button */}
                                                    <div className='self-end'>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveSocialLink(index)}
                                                            className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500"
                                                        >
                                                            <Icon icon="mdi-light:delete" className="text-xl" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}

                                            {/* Add Button */}
                                            <button
                                                type="button"
                                                onClick={handleAddSocialLink}
                                                className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-blue-600 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                            >
                                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                </svg>
                                                Add Social Media Link
                                            </button>
                                        </div>
                                    </div>

                                    {/* Submit Button */}
                                    <div className="flex items-center">
                                        <button
                                            type="submit"
                                            disabled={isLoading}
                                            className="px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                                        >
                                            {isLoading ? 'Updating...' : 'Update'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                )}
            </div>
            {/* loading modal spinner while saving */}
            {isLoading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-4 rounded-lg flex flex-col items-center shadow-lg">
                        <svg
                            className="animate-spin h-8 w-8 text-blue-600 mb-3"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            ></circle>
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                            ></path>
                        </svg>

                        <p className="text-sm font-medium text-gray-700">Saving...</p>
                    </div>
                </div>
            )}
            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            <FooterTailwind />
        </TailwindLayout>
    )
}